import { Container, Graphics, Text } from 'pixi.js';

import { BaseWidget } from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';

/**
 * Altitude tape widget that displays a scrolling vertical tape similar to the Garmin G5.
 * Shows major graduations every 100 feet and minor divisions every 25 feet.
 * The tape scrolls vertically as altitude changes, with approximately 300 feet visible in the viewport.
 */
export class AltitudeTapeWidget extends BaseWidget {
    static readonly requiredProperties = ['altitudeFeet'] as const;
    static readonly optionalProperties = [] as const;

    private container: Container;
    private tapeGraphics: Graphics;
    private labelsContainer: Container;

    // Configuration constants
    private readonly PIXELS_PER_FOOT = 0.75; // Scale factor for tape movement
    private readonly MAJOR_INTERVAL = 100; // Major graduations every 100 feet
    private readonly MINOR_INTERVAL = 25; // Minor divisions every 25 feet
    private readonly TAPE_BUFFER = 500; // Draw this many feet above/below viewport
    private readonly MAJOR_TICK_WIDTH = 10;
    private readonly MINOR_TICK_WIDTH = 5;

    private currentAltitude = 0;
    private lastDrawnAltitude = 0;

    async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        this.container = elementContainer;

        // Create graphics object for the tape
        this.tapeGraphics = new Graphics();
        this.labelsContainer = new Container();

        // Create viewport mask to clip the tape to the visible area
        // This ensures only ~300 feet of the tape is visible at any time
        const viewportMask = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(0xffffff);
        elementContainer.addChild(viewportMask);
        elementContainer.mask = viewportMask;

        // Add tape and labels to container
        elementContainer.addChild(this.tapeGraphics);
        elementContainer.addChild(this.labelsContainer);
    }

    protected updateWidget(
        point: CurrentPoint,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        this.currentAltitude = point.altitudeFeet;

        // Redraw the tape if altitude has changed significantly or if this is the first draw
        // This maintains the buffer of visible graduations
        if (
            this.lastDrawnAltitude === 0 ||
            Math.abs(this.currentAltitude - this.lastDrawnAltitude) > 200
        ) {
            this.drawTape(hudContext, positionTranslator);
            this.lastDrawnAltitude = this.currentAltitude;
        }

        // Update tape position to create scrolling effect
        this.updateTapePosition();
    }

    /**
     * Draws the altitude tape with major and minor graduations.
     * Draws a range centered on current altitude to provide buffer for scrolling.
     */
    private drawTape(
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        // Clear existing graphics
        this.tapeGraphics.clear();
        this.labelsContainer.removeChildren();

        const color = 'white';
        const centerY = positionTranslator.y(0.5);

        // Calculate altitude range to draw
        const minAltitude =
            Math.floor(
                (this.currentAltitude - this.TAPE_BUFFER) / this.MINOR_INTERVAL
            ) * this.MINOR_INTERVAL;
        const maxAltitude =
            Math.ceil(
                (this.currentAltitude + this.TAPE_BUFFER) / this.MINOR_INTERVAL
            ) * this.MINOR_INTERVAL;

        // Spine on far left side, tick marks and labels extend to the right
        const spineX = 0; // Fixed position in pixels from left edge
        // Draw all graduations
        for (
            let altitude = minAltitude;
            altitude <= maxAltitude;
            altitude += this.MINOR_INTERVAL
        ) {
            const isMajor = altitude % this.MAJOR_INTERVAL === 0;
            const tickWidth = isMajor
                ? this.MAJOR_TICK_WIDTH
                : this.MINOR_TICK_WIDTH;

            // Calculate Y position relative to center
            const altitudeDiff = altitude - this.currentAltitude;
            const y = centerY - altitudeDiff * this.PIXELS_PER_FOOT;

            // Add tick mark to path - extend to the RIGHT
            this.tapeGraphics.moveTo(spineX, y);
            this.tapeGraphics.lineTo(spineX + tickWidth, y);

            // Draw label for major graduations - position to the RIGHT of tick
            if (isMajor) {
                const label = new Text({
                    text: altitude.toString(),
                    style: {
                        fontFamily: hudContext.globalConfig.fontFamily,
                        fontSize: hudContext.globalConfig.baseFontSize + 2,
                        fill: color,
                    },
                });
                label.anchor.set(0, 0.5);
                label.position.set(spineX + tickWidth + 5, y);
                this.labelsContainer.addChild(label);
            }
        }

        // Stroke all the lines at once
        this.tapeGraphics.stroke({ width: 2, color });
    }

    /**
     * Updates the position of the tape graphics to create scrolling effect.
     * The tape moves down as altitude increases and up as altitude decreases.
     */
    private updateTapePosition(): void {
        const altitudeDiff = this.currentAltitude - this.lastDrawnAltitude;
        const offset = altitudeDiff * this.PIXELS_PER_FOOT;

        // Move both the graphics and labels together
        // Positive offset (ascending) moves tape down, negative (descending) moves up
        this.tapeGraphics.position.y = offset;
        this.labelsContainer.position.y = offset;
    }

    cleanUp(): void {
        this.tapeGraphics?.destroy();
        this.labelsContainer?.destroy();
        this.container?.destroy();

        this.tapeGraphics = null;
        this.labelsContainer = null;
        this.container = null;
    }
}
